import React, { createContext, ReactNode, useContext, useState, useEffect } from 'react'
import { useTeamMembersApi } from 'apps/fleet-planner/hooks/useTeamMembersApi'
import { ITeamMember, TManageTeamMemberModalMode } from 'apps/fleet-planner/types'
import { UserContext } from './UserContext'

export interface ITeamMemberContextState {
  isFetching: boolean
  teamMembers: ITeamMember[]
  setTeamMembers: React.Dispatch<React.SetStateAction<ITeamMember[]>>
  filteredTeamMembers: ITeamMember[]
  setFilteredTeamMembers: React.Dispatch<React.SetStateAction<ITeamMember[]>>
  selectedTeamMember: ITeamMember | undefined
  setSelectedTeamMember: React.Dispatch<React.SetStateAction<ITeamMember | undefined>>
  currentTeamMember: ITeamMember | undefined
  setCurrentTeamMember: React.Dispatch<React.SetStateAction<ITeamMember | undefined>>
  modalMode: TManageTeamMemberModalMode | undefined
  setModalMode: React.Dispatch<React.SetStateAction<TManageTeamMemberModalMode | undefined>>
}

export const TeamMemberContext = createContext<ITeamMemberContextState>({
  isFetching: false,
  teamMembers: [],
  setTeamMembers: () => [],
  filteredTeamMembers: [],
  setFilteredTeamMembers: () => [],
  selectedTeamMember: undefined,
  setSelectedTeamMember: () => undefined,
  currentTeamMember: undefined,
  setCurrentTeamMember: () => undefined,
  modalMode: undefined,
  setModalMode: () => undefined,
})

export const TeamMembersProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const { getTeamMembers } = useTeamMembersApi()
  const [isFetching, setIsFetching] = useState<boolean>(true)
  const [teamMembers, setTeamMembers] = useState<ITeamMember[]>([])
  const [filteredTeamMembers, setFilteredTeamMembers] = useState<ITeamMember[]>([])
  const [selectedTeamMember, setSelectedTeamMember] = useState<ITeamMember | undefined>()
  const [currentTeamMember, setCurrentTeamMember] = useState<ITeamMember | undefined>()
  const [modalMode, setModalMode] = useState<TManageTeamMemberModalMode | undefined>()
  const { userData } = useContext(UserContext)
  const { user } = userData || {}

  useEffect(() => {
    getTeamMembers()
      .then(fetchedTeamMembers => {
        if (fetchedTeamMembers) {
          const currentUser = fetchedTeamMembers.find(teamMember => teamMember.id === user?.id)
          setCurrentTeamMember(currentUser)
          setTeamMembers(fetchedTeamMembers)
        }
      })
      .finally(() => {
        setIsFetching(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const state = {
    isFetching,
    teamMembers,
    setTeamMembers,
    filteredTeamMembers,
    setFilteredTeamMembers,
    selectedTeamMember,
    setSelectedTeamMember,
    currentTeamMember,
    setCurrentTeamMember,
    modalMode,
    setModalMode,
  }

  return <TeamMemberContext.Provider value={state}>{children}</TeamMemberContext.Provider>
}

export const useTeamMemberContext = () => useContext(TeamMemberContext)
